import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, SelectHTMLAttributes } from 'react';
import { Icon } from '../../icons/Icon';
import { useFieldControl } from '../Field/fieldContext';
import type { InputSize } from '../Input/Input';
import styles from './Select.module.css';

export interface SelectOption {
    readonly value: string;
    /** Sentence case, per §9. */
    readonly label: string;
}

/* §5: "Same border/height family as Input". Aliasing Input's type rather than declaring a parallel
   one is what makes that literally true instead of true today — a size added to Input arrives here,
   and one removed from it stops compiling here. */
export type SelectSize = InputSize;

type NativeSelectProps = Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    'className' | 'disabled' | 'size' | 'children'
>;

export interface SelectProps extends NativeSelectProps {
    readonly options: readonly SelectOption[];
    /**
     * The unchosen state, rendered as a disabled empty option so it can be shown but not selected
     * back into. Omit it for a select that always has a value.
     */
    readonly placeholder?: string;
    readonly size?: SelectSize;
    /** Rendered as `aria-disabled` plus intercepted input — see the component body. */
    readonly disabled?: boolean;
    readonly invalid?: boolean;
    readonly 'data-testid'?: string;
}

/**
 * A real `<select>` (FS-3 FR-C2), with the chrome §5 asks for: Input's border and height family, and our
 * own chevron in place of the platform's.
 *
 * Options are a typed array rather than `children`, so the two things a caller could otherwise get
 * wrong — arbitrary markup inside a `<select>`, and an `<option>` with no stable value — are not
 * expressible. `<optgroup>` is genuinely absent rather than forgotten: nothing this slice ships
 * groups its options, and §0's rule of two says it arrives with the second screen that needs it.
 *
 * **Disabled is `aria-disabled` plus intercepted input, never the bare attribute** (§5's matrix,
 * §9's permission rule — the control must stay focusable so its reason is discoverable). A select
 * has no `readOnly`, so the two routes a user has to change one are blocked instead: `mousedown`
 * (which is what opens the list) and `keydown` (arrows and type-ahead). Suppressing `change`
 * afterwards would be too late — the value would already have moved on screen.
 */
export function Select({
    options,
    placeholder,
    size = 'form',
    disabled = false,
    invalid,
    id,
    required,
    'aria-describedby': describedBy,
    'data-testid': testId,
    onBlur,
    onChange,
    onKeyDown,
    onMouseDown,
    ...rest
}: SelectProps) {
    const field = useFieldControl({ id, 'aria-describedby': describedBy, invalid, required });

    function handleBlur(event: FocusEvent<HTMLSelectElement>) {
        onBlur?.(event);
        field.reportBlur(event.target.value);
    }

    function handleChange(event: ChangeEvent<HTMLSelectElement>) {
        onChange?.(event);
        field.reportChange(event.target.value);
    }

    function handleMouseDown(event: MouseEvent<HTMLSelectElement>) {
        if (disabled) {
            event.preventDefault();
            return;
        }
        onMouseDown?.(event);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLSelectElement>) {
        if (disabled) {
            // Tab must still get through, or the control stops being focusable in practice — which
            // is the property §9's permission rule turns on.
            if (event.key !== 'Tab') {
                event.preventDefault();
            }
            return;
        }
        onKeyDown?.(event);
    }

    return (
        <span className={`${styles.shell} ${styles[size]}`}>
            <select
                {...rest}
                aria-describedby={field['aria-describedby']}
                aria-disabled={disabled}
                aria-invalid={field['aria-invalid']}
                className={styles.select}
                data-testid={testId}
                id={field.id}
                onBlur={handleBlur}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onMouseDown={handleMouseDown}
                required={field.required}
            >
                {placeholder === undefined ? null : (
                    // `disabled` on an OPTION is the one place the bare attribute is right: §5's
                    // rule is about controls a user may not operate, and this is a value that must
                    // not be chosen — the select itself stays fully operable.
                    <option disabled value="">
                        {placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            <span className={styles.chevron}>
                <Icon name="expand" size="sm" />
            </span>
        </span>
    );
}
